import type { JourneyAwards, JourneyProfile, Material } from '@facecontrol/shared';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Toast } from './components/Toast';
import { BottomNav, Footer, MemberHeader, type Section } from './components/Header';
import { liftCardIntoPlayer } from './fx/transition';
import { createPaymentLink, getConfig, getJourney, getMaterials, getMe, sendEvent, submitBug } from './lib/api';
import { byDate, dateDots, monthKey } from './lib/format';
import { ApiError } from './lib/http';
import { reducedMotion } from './lib/motion';
import { useRouter } from './lib/router';
import { loadStoredSession, login, logout, refresh, storeSession, type Session, withAuth } from './lib/session';
import { getTgEnv, haptic, onBackButton, openExternal, showBackButton } from './lib/tg';
import { Catalog } from './screens/Catalog';
import { MaterialScreen } from './screens/MaterialScreen';
import { ErrorScreen, Paywall } from './screens/States';

const Profile = lazy(() => import('./screens/Profile'));
const Landing = lazy(() => import('./site/Landing'));
const CertCheck = lazy(() => import('./site/CertCheck'));


type Phase = { kind: 'booting' } | { kind: 'guest' } | { kind: 'ready'; session: Session } | { kind: 'error'; title: string; text: string };

interface AppConfig {
  botUsername: string | null;
  supportUser: string | null;
  salesOpen: boolean;
}

const DEV_BYPASS = import.meta.env.DEV && new URLSearchParams(location.search).has('initData');

function describeError(e: unknown): { title: string; text: string } {
  if (e instanceof ApiError) {
    if (e.status === 401) return { title: 'Не удалось подтвердить вход', text: 'Закройте приложение и откройте его заново из бота.' };
    if (e.status === 403) return { title: 'Доступ закрыт', text: 'Ваш аккаунт заблокирован. Напишите в поддержку, если это ошибка.' };
    if (e.status >= 500) return { title: 'Сервер отдыхает', text: 'Мы уже знаем и чиним. Попробуйте через минуту.' };
    return { title: 'Ошибка запроса', text: e.message };
  }
  if (e instanceof DOMException && e.name === 'TimeoutError') return { title: 'Долгий ответ', text: 'Сеть отвечает слишком медленно. Проверьте соединение и попробуйте снова.' };
  return { title: 'Нет соединения', text: 'Проверьте интернет и попробуйте ещё раз.' };
}

export function App() {
  const tg = getTgEnv();
  const initDataRaw = tg.initDataRaw ?? (DEV_BYPASS ? new URLSearchParams(location.search).get('initData') : null);
  const [phase, setPhase] = useState<Phase>({ kind: 'booting' });
  const [materials, setMaterials] = useState<Material[] | null>(null);
  const [config, setConfig] = useState<AppConfig>({ botUsername: null, supportUser: null, salesOpen: false });
  const [entering, setEntering] = useState(false);
  const [returning, setReturning] = useState(false);
  const returnTo = useRef<{ id: string; scrollY: number } | null>(null);
  const { route, navigate, back } = useRouter();
  const sessionRef = useRef<Session | null>(null);
  const [journey, setJourney] = useState<JourneyProfile | null>(null);
  const [journeyError, setJourneyError] = useState(false);
  const [toast, setToast] = useState<{ id: number; text: string; sub?: string } | null>(null);
  const [paying, setPaying] = useState(false);
  const setSession = useCallback((s: Session) => {
    sessionRef.current = s;
    setPhase({ kind: 'ready', session: s });
  }, []);

  // Session bootstrap: one request to our own API, then the catalog. Nothing throws out of here.
  const boot = useCallback(async () => {
    if (!initDataRaw) {
      // The desktop site: a stored session from «Войти через Telegram», otherwise the landing page.
      const stored = loadStoredSession();
      if (!stored) return setPhase({ kind: 'guest' });
      try {
        setSession(await refresh(stored));
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          storeSession(null);
          return setPhase({ kind: 'guest' });
        }
        setPhase({ kind: 'error', ...describeError(e) });
      }
      return;
    }
    setPhase({ kind: 'booting' });
    try {
      const stored = loadStoredSession();
      let s: Session;
      if (stored) {
        try {
          s = await refresh(stored);
        } catch {
          s = await login(initDataRaw);
        }
      } else {
        s = await login(initDataRaw);
      }
      setSession(s);
    } catch (e) {
      setPhase({ kind: 'error', ...describeError(e) });
    }
  }, [initDataRaw, setSession]);

  useEffect(() => {
    void boot();
  }, [boot]);

  // Config (bot username, sales window) is cosmetic: it never blocks rendering.
  useEffect(() => {
    getConfig()
      .then(setConfig)
      .catch(() => {});
  }, []);

  // Catalog follows the session.
  const active = phase.kind === 'ready' && phase.session.me.subscription.active;
  useEffect(() => {
    if (phase.kind !== 'ready') return;
    if (!active) {
      setMaterials([]);
      return;
    }
    let cancelled = false;
    withAuth(
      () => sessionRef.current!,
      setSession,
      (t) => getMaterials(t),
    )
      .then((list) => {
        if (!cancelled) setMaterials(list);
      })
      .catch((e) => {
        if (!cancelled) setPhase({ kind: 'error', ...describeError(e) });
      });
    return () => {
      cancelled = true;
    };
  }, [phase.kind, active, setSession]);

  // «Путь»: loaded after the catalog, never blocks it. A failure only affects the profile.
  const loadJourney = useCallback(async () => {
    if (!sessionRef.current) return null;
    setJourneyError(false);
    try {
      const j = await withAuth(() => sessionRef.current!, setSession, (t) => getJourney(t));
      setJourney(j);
      return j;
    } catch {
      setJourneyError(true);
      return null;
    }
  }, [setSession]);
  useEffect(() => {
    if (phase.kind !== 'ready' || !active) return;
    void loadJourney();
  }, [phase.kind, active, loadJourney]);

  // A lesson finished for the first time (it earned something): a quiet plaque, no fanfare (03.11 review).
  const onAwards = useCallback(
    (a: JourneyAwards | null) => {
      if (!a || a.glow === 0) return;
      void loadJourney().then((j) => setToast({ id: Date.now(), text: 'Урок пройден', sub: j ? `Всего пройдено: ${j.lessonsCompleted}` : undefined }));
    },
    [loadJourney],
  );

  // «Моя подписка»: the session's `me` is refreshed when the member comes back from the payment page.
  const refreshMe = useCallback(async () => {
    const s = sessionRef.current;
    if (!s) return;
    try {
      const me = await withAuth(() => sessionRef.current!, setSession, (t) => getMe(t));
      setSession({ ...sessionRef.current!, me });
    } catch {
      /* the old state stays on screen */
    }
  }, [setSession]);
  useEffect(() => {
    const onVisible = () => document.visibilityState === 'visible' && void refreshMe();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshMe]);

  // 🐞 owners only (TELEGRAM_ADMIN_IDS): the widget loads in its own chunk, members never download it
  const isOwner = phase.kind === 'ready' && !!phase.session.me.isAdmin;
  useEffect(() => {
    if (!isOwner) return;
    let unmount: (() => void) | null = null;
    let gone = false;
    void import('@facecontrol/shared/bugkit').then(({ mountBugReporter }) => {
      if (gone) return;
      unmount = mountBugReporter({
        submit: (r) => withAuth(() => sessionRef.current!, setSession, (t) => submitBug(t, { ...r, source: tg.inTelegram ? 'app' : 'site' })),
        platform: tg.platform,
        bottom: window.innerWidth < 960 ? 80 : 16,
      });
    });
    return () => {
      gone = true;
      unmount?.();
    };
  }, [isOwner, setSession, tg.inTelegram, tg.platform]);

  const pay = useCallback(async () => {
    if (!sessionRef.current || paying) return;
    setPaying(true);
    try {
      const { url } = await withAuth(() => sessionRef.current!, setSession, (t) => createPaymentLink(t));
      openExternal(url);
      setToast({ id: Date.now(), text: 'Открыли страницу оплаты', sub: 'После оплаты вернитесь сюда — доступ обновится сам' });
    } catch (e) {
      const code = e instanceof ApiError ? e.status : 0;
      if (code === 409 && e instanceof ApiError && e.code === 'already_paid') {
        setToast({ id: Date.now(), text: 'Уже оплачено', sub: 'Следующий месяц у вас есть — платить повторно не нужно' });
        void refreshMe();
      } else if (code === 409) {
        setToast({ id: Date.now(), text: 'Продажи закрыты', sub: 'Оплатить можно с 1 по 5 число' });
      } else if (config.botUsername) {
        // payments are off here (test shop not connected) or the provider failed: the bot can still take it
        openExternal(`https://t.me/${config.botUsername}?start=pay`);
      } else {
        setToast({ id: Date.now(), text: 'Оплата не открылась', sub: 'Попробуйте ещё раз через минуту' });
      }
    } finally {
      setPaying(false);
    }
  }, [paying, setSession, refreshMe, config.botUsername]);

  // Telegram back button mirrors the router; leaving the player is the lift in reverse (short, then history.back).
  const leaveMaterial = useCallback(() => {
    const el = document.querySelector('.material');
    if (!el || reducedMotion()) return back();
    el.classList.add('is-leaving');
    setTimeout(back, 180);
  }, [back]);
  useEffect(() => {
    showBackButton(route.name !== 'catalog');
    return onBackButton(route.name === 'material' ? leaveMaterial : back);
  }, [route.name, back, leaveMaterial]);
  // Coming back to the catalog: rise from depth and land on the card that was opened.
  useEffect(() => {
    if (route.name !== 'catalog') return;
    const r = returnTo.current;
    if (!r) return;
    returnTo.current = null;
    setReturning(true);
    setEntering(false);
    requestAnimationFrame(() => window.scrollTo(0, r.scrollY));
    const t = setTimeout(() => setReturning(false), 500);
    return () => clearTimeout(t);
  }, [route.name]);

  const openMaterial = useCallback(
    async (m: Material, el: HTMLElement) => {
      haptic('light');
      setEntering(true);
      returnTo.current = { id: m.id, scrollY: window.scrollY };
      // the lift is decoration: if the WebView cannot animate (or stalls), navigate anyway
      await Promise.race([liftCardIntoPlayer(el).catch(() => {}), new Promise((r) => setTimeout(r, 900))]);
      navigate({ name: 'material', id: m.id });
      window.scrollTo(0, 0);
    },
    [navigate],
  );

  const track = useCallback(
    (materialId: string) => (kind: 'open' | 'progress' | 'complete', position?: number) => {
      const s = sessionRef.current;
      if (!s) return;
      void withAuth(
        () => sessionRef.current!,
        setSession,
        (t) => sendEvent(t, { materialId, kind, position }),
      )
        .then(onAwards)
        .catch(() => {});
    },
    [setSession, onAwards],
  );
  const trackers = useRef(new Map<string, ReturnType<typeof track>>());
  const trackerFor = (id: string) => {
    let fn = trackers.current.get(id);
    if (!fn) {
      fn = track(id);
      trackers.current.set(id, fn);
    }
    return fn;
  };

  useEffect(() => {
    document.body.classList.toggle('is-web', !tg.inTelegram);
  }, [tg.inTelegram]);

  const isWeb = !tg.inTelegram;
  const openSubscription = () => navigate({ name: 'profile' });
  const supportUrl = config.supportUser ? `https://t.me/${config.supportUser.replace(/^@/, '')}` : config.botUsername ? `https://t.me/${config.botUsername}` : null;
  if (route.name === 'verify')
    return (
      <Suspense fallback={<main className="screen" aria-busy="true" />}>
        <CertCheck code={route.code} />
      </Suspense>
    );
  if (phase.kind === 'guest')
    return (
      <Suspense fallback={<main className="screen" aria-busy="true" />}>
        <Landing onLogin={setSession} startWithLogin={new URLSearchParams(location.search).has('login')} />
      </Suspense>
    );
  if (phase.kind === 'error') return <ErrorScreen title={phase.title} text={phase.text} onRetry={boot} />;
  if (phase.kind === 'booting') return <Catalog me={null} materials={null} returning={false} onOpen={() => {}} />;
  const months = new Map<string, Material[]>();
  for (const m of materials ?? []) {
    const k = monthKey(new Date(m.createdAt));
    months.set(k, [...(months.get(k) ?? []), m]);
  }
  const toastEl = toast && createPortal(<Toast key={toast.id} text={toast.text} sub={toast.sub} onDone={() => setToast(null)} />, document.body);
  if (!active)
    return (
      <>
        <Paywall botUsername={config.botUsername} salesOpen={phase.session.me.sales?.open ?? config.salesOpen} firstName={phase.session.me.user.firstName} paying={paying} onPay={() => void pay()} />
        {toastEl}
      </>
    );

  const me = phase.session.me;
  const endTime = me.subscription.endTime;

  let screen: React.ReactElement;
  if (route.name === 'material') {
    const m = materials?.find((x) => x.id === route.id);
    if (materials && !m) screen = <ErrorScreen dark title="Урок не найден" text="Возможно, он ещё не опубликован или уже убран из программы." onRetry={() => navigate({ name: 'catalog' })} />;
    else if (!m) screen = <div className="material" aria-busy="true" />;
    else
      screen = (
        <MaterialScreen
          key={m.id}
          material={m}
          entering={entering}
          progress={journey?.progress[m.id]}
          siblings={byDate(months.get(monthKey(new Date(m.createdAt))) ?? [m])}
          track={trackerFor(m.id)}
          onLessons={() => navigate({ name: 'catalog' })}
          onOpenOther={(x) => {
            navigate({ name: 'material', id: x.id });
            window.scrollTo(0, 0);
          }}
        />
      );
  } else if (route.name === 'profile') {
    screen = (
      <Suspense fallback={<main className="screen profile" aria-busy="true"><div className="sk sk--card" /></main>}>
        <Profile me={me} profile={journeyError ? null : journey} supportUrl={supportUrl} paying={paying} onPay={() => void pay()} />
      </Suspense>
    );
  } else {
    screen = <Catalog me={me} materials={materials} journey={journey} returning={returning} onOpen={openMaterial} onSubscription={openSubscription} />;
  }
  const section: Section = route.name === 'catalog' ? 'lessons' : route.name === 'profile' ? 'sub' : null;
  const name = me.user.firstName ?? (me.user.username ? `@${me.user.username}` : 'Участница');
  return (
    <div className={`shell${section ? ' has-bnav' : ''}`}>
      <MemberHeader
        active={section ?? (route.name === 'material' ? 'lessons' : null)}
        name={name}
        isWeb={isWeb}
        back={isWeb && route.name !== 'catalog' ? { label: route.name === 'material' ? 'Уроки' : 'Назад', onClick: route.name === 'material' ? leaveMaterial : back } : null}
        onLessons={() => navigate({ name: 'catalog' })}
        onSubscription={openSubscription}
        onLogout={() => {
          void logout(sessionRef.current);
          sessionRef.current = null;
          setJourney(null);
          setPhase({ kind: 'guest' });
          navigate({ name: 'catalog' });
        }}
      />
      {screen}
      <Footer status={endTime ? `Подписка активна до ${dateDots(endTime)}` : 'Подписка активна'} support={supportUrl} />
      {section && <BottomNav active={section} onLessons={() => navigate({ name: 'catalog' })} onSubscription={openSubscription} />}
      {toastEl}
    </div>
  );
}
